import { MutationTree } from "vuex";
import {
  IOrganizationState,
  TravelerProfileSummary,
} from "@/core/interfaces/organizationState.interface";
import { IOrganizationStats } from "@/core/interfaces/organizationStatistics.interface";
import { IOrganizationProductivity } from "@/core/interfaces/organizationProductivity.interface";
import {
  IOrganization,
  ISaasEliteOrganization,
  OperationsUser,
} from "@/core/interfaces/organization.interface";
import { IOrganizationDetail } from "@/core/interfaces/organizationDetail.interface";
import { IAgentV2, IAgentDetails } from "@/core/interfaces/agent.interface";
import { IFinancialProfile } from "@/core/interfaces/financialProfile.interface";
import { IWhatsappMember } from "@/core/interfaces/whatsappMember.interface";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { CurrencyConversion } from "@/core/types/index";
import { ISalesSector } from "@/core/interfaces/salesProductivity.interface";
import {
  PricingCreationForm,
  PricingItem,
} from "@/core/interfaces/managePricing.interface";
import {
  OrganizationPassengerSetupForm,
  OrganizationPassengerSetupFormV2,
} from "../models/OrganizationPassengerSetupForm.reponse";
import { ComboboxOptions } from "@/types/proxima-design-system";
import { IEmployeeGrade } from "@/core/interfaces/employeeGrade.interface";
import { ICostCenter } from "@/core/interfaces/costCenter.interface";
import { IDepartment } from "@/core/interfaces/department.interface";
import { IPaginationMeta } from "@/core/interfaces/state/paginationMeta.interface";

const mutations: MutationTree<IOrganizationState> = {
  enableCreateOrganizationLoading(state) {
    state.isCreatingOrganization = true;
  },
  disableCreateOrganizationLoading(state) {
    state.isCreatingOrganization = false;
  },
  enableUpdateOrganizationLoading(state) {
    state.isUpdatingOrganization = true;
  },
  disableUpdateOrganizationLoading(state) {
    state.isUpdatingOrganization = false;
  },
  enableFetchOrganizationsLoading(state) {
    state.isFetchingOrganizations = true;
  },
  enableFetchOrganizationLoading(state) {
    state.isFetchingOrganization = true;
    state.organization = null;
  },
  disableFetchOrganizationLoading(state) {
    state.isFetchingOrganization = false;
  },
  setFetchingOrganization(state, payload: boolean) {
    state.isFetchingOrganization = payload;
  },
  enableAddAgentLoading(state) {
    state.isAddingAgent = true;
  },
  disableAddAgentLoading(state) {
    state.isAddingAgent = false;
  },
  setIsValidatingBulkUsers(state, payload: boolean) {
    state.isValidatingBulkUsers = payload;
  },
  setIsImportingBulkUsers(state, payload: boolean) {
    state.isImportingBulkUsers = payload;
  },
  setBulkUserValidateResult(state, payload: unknown | null) {
    state.bulkUserValidateResult = payload;
  },
  setBulkUserImportResult(state, payload: unknown | null) {
    state.bulkUserImportResult = payload;
  },
  clearBulkUserImportResults(state) {
    state.bulkUserValidateResult = null;
    state.bulkUserImportResult = null;
  },
  enablePricingFormSubmissionPending(state) {
    state.isPricingFormSubmissionPending = true;
  },
  disablePricingFormSubmissionPending(state) {
    state.isAddingAgent = false;
  },
  setOrganizationEmployeeGrades(state, payload: IEmployeeGrade[]) {
    state.organizationEmployeeGrades = payload;
  },
  setOrganizationCostCenters(state, payload: ICostCenter[]) {
    state.organizationCostCenters = payload;
  },
  setOrganizationDepartments(state, payload: IDepartment[]) {
    state.organizationDepartments = payload;
  },
  setIsFetchingOrganizationEmployeeGrades(state, payload: boolean) {
    state.isFetchingOrganizationEmployeeGrades = payload;
  },
  setIsFetchingOrganizationCostCenters(state, payload: boolean) {
    state.isFetchingOrganizationCostCenters = payload;
  },
  setIsFetchingOrganizationDepartments(state, payload: boolean) {
    state.isFetchingOrganizationDepartments = payload;
  },
  enableFetchPricingListLoading(state) {
    state.isFetchPricinglistLoading = true;
  },
  disableFetchPricingListLoading(state) {
    state.isFetchPricinglistLoading = false;
  },
  saveOrganizations(state, organizations: IOrganization[]) {
    state.isFetchingOrganizations = false;
    state.organizations = organizations;
  },
  savePricingForm(state, pricingForm: PricingCreationForm) {
    state.isFetchPricingFormLoading = false;
    state.pricingFormDetails = pricingForm;
  },
  savePricingList(state, pricingList: PricingItem[]) {
    state.isFetchPricinglistLoading = false;
    state.pricingList = pricingList;
  },
  saveOrganization(state, organization: IOrganizationDetail) {
    state.organization = organization;
  },
  saveSelectedOrganizationId(state, fp: string) {
    state.selectedOrganizationId = fp;
  },
  updateOrganizationStateChange(state, isInProgress: boolean) {
    state.isUpdatingOrganization = isInProgress;
  },
  updateAgents(state, agents: IAgentV2[]) {
    if (state.organization) {
      state.organization.organization_users = agents;
    }
    state.isAddingAgent = false;
  },
  appendFinancialProfileInOrganization(
    state,
    financialProfile: IFinancialProfile,
  ) {
    state.organization?.financial_profiles.push(financialProfile);
  },
  removeFinancialProfileFromOrganization(state, id: string) {
    if (state.organization) {
      let tempFinancialProfiles: IFinancialProfile[] = [
        ...state.organization.financial_profiles,
      ];
      tempFinancialProfiles = tempFinancialProfiles.filter(
        (financialProfile: IFinancialProfile) =>
          financialProfile.public_id !== id,
      );
      state.organization.financial_profiles = tempFinancialProfiles;
    }
  },
  updateStatus(state, organization: IOrganizationDetail) {
    state.isUpdatingOrganization = false;
    if (state.organization) {
      state.organization.status = organization.status;
    }
  },
  resetOrganizationState(state) {
    state.isAddingAgent = false;
    state.isFetchingOrganization = false;
    state.organization = null;
    state.organizations = [];
    state.isFetchingOrganizations = false;
    state.isCreatingOrganization = false;
    state.isUpdatingOrganization = false;
    state.isDeletingAgentFromOrganization = false;
  },
  enableIsDeletingAgentFromOrganization(state) {
    state.isDeletingAgentFromOrganization = true;
  },
  disableIsDeletingAgentFromOrganization(state) {
    state.isDeletingAgentFromOrganization = false;
  },
  deleteAgentFromOrganization(state, agentId: string) {
    if (state.organization?.organization_users) {
      const agents = [...state.organization.organization_users];
      const deletedAgentIndex = agents
        .map((agent) => agent.organization_user_id)
        .indexOf(agentId);

      agents.splice(deletedAgentIndex, 1);
      state.organization.organization_users = agents;
    }
  },
  setUpdateWebsiteConfigurationLoading(state, payload: boolean) {
    state.isUpdateWebsiteConfigurationLoading = payload;
  },
  setWebsiteConfigLoading(state, payload: boolean) {
    state.isWebsiteConfigLoading = payload;
  },
  setWebsiteConfig(state, payload: any) {
    state.websiteConfig = payload;
  },
  setWebsiteContent(state, payload: any) {
    state.websiteContent = payload;
  },
  setUpdateWebsiteContentLoading(state, payload: boolean) {
    state.isUpdateWebsiteContentLoading = payload;
  },
  setWhatsappActionLoading(state, payload: boolean) {
    state.whatsappActionLoading = payload;
  },
  setGenerateOTPLoading(state, payload: boolean) {
    state.isGenerateOTPLoading = payload;
  },
  setWhatsappMembersLoading(state, payload: boolean) {
    state.isWhatsappMembersLoading = payload;
  },
  setWhatsMembers(
    state,
    payload: {
      results: IWhatsappMember[];
      count: number;
      next: string | null;
      previous: string | null;
      page: number;
      pageSize: number;
    },
  ) {
    state.whatsappMembers = payload.results;
    state.whatsappMembersCount = payload.count;
    state.whatsappMembersNext = payload.next;
    state.whatsappMembersPrevious = payload.previous;
    state.whatsappMembersCurrentPage = payload.page;
    state.whatsappMembersPageSize = payload.pageSize;
  },
  resetWhatsMembers(state) {
    state.whatsappMembers = [];
    state.whatsappMembersCount = 0;
    state.whatsappMembersNext = null;
    state.whatsappMembersPrevious = null;
    state.whatsappMembersCurrentPage = 1;
    state.whatsappMembersPageSize = 10;
  },
  fetchingPassengers(state, payload: boolean) {
    state.isFetchingPassengers = payload;
  },
  fetchingPassengerSetupForm(state, payload: boolean) {
    state.isFetchingPassengerSetupForm = payload;
  },
  saveOrganizationPassengerSetupForm(
    state,
    payload: OrganizationPassengerSetupForm,
  ) {
    state.organizationPassengerSetupForm = payload;
  },
  saveOrganizationPassengerSetupFormV2(
    state,
    payload: OrganizationPassengerSetupFormV2,
  ) {
    state.organizationPassengerSetupFormV2 = payload;
  },
  saveReferenceOptions(
    state: IOrganizationState,
    payload: { url: string; options: ComboboxOptions[] },
  ) {
    state.referenceOptions = {
      ...state.referenceOptions,
      [payload.url]: payload.options,
    };
    state.fetchingReferenceUrls.add(payload.url);
  },

  markReferenceUrlFetching(state: IOrganizationState, url: string) {
    state.fetchingReferenceUrls.add(url);
  },
  setPassengerActionLoading(state, payload: boolean) {
    state.passengerActionLoading = payload;
  },
  setPassengers(state, payload: IPassenger[]) {
    state.passengers = payload;
  },
  resetTravelerProfiles(state) {
    state.travelerProfiles = [];
    state.travelerProfilesPaginationMeta = null;
    state.travelerProfilesSearchQuery = "";
    state.isFetchingTravelerProfiles = false;
  },
  setTravelerProfiles(state, payload: TravelerProfileSummary[]) {
    state.travelerProfiles = payload;
  },
  setTravelerProfilesPaginationMeta(state, payload: IPaginationMeta | null) {
    state.travelerProfilesPaginationMeta = payload;
  },
  setTravelerProfilesSearchQuery(state, payload: string) {
    state.travelerProfilesSearchQuery = payload;
  },
  setFetchingTravelerProfiles(state, payload: boolean) {
    state.isFetchingTravelerProfiles = payload;
  },
  setOrganizationUsers(state, payload: IAgentDetails[]) {
    state.organizationUsers = payload;
  },
  setSearchingOrganizationUsers(state, payload: boolean) {
    state.isSearchingOrganizationUsers = payload;
  },
  resetOrganizationUsers(state) {
    state.organizationUsers = [];
    state.isSearchingOrganizationUsers = false;
  },
  updatePassenger(
    state,
    payload: { passengerId: string; passenger: IPassenger },
  ) {
    if (state.passengers) {
      state.passengers = state.passengers.map((item: IPassenger) =>
        item.public_id === payload.passengerId
          ? { ...payload.passenger, public_id: item.public_id }
          : item,
      );
    }

    if (state.activePassenger?.public_id === payload.passengerId) {
      state.activePassenger = {
        ...payload.passenger,
        public_id: payload.passengerId,
      };
    }
  },
  addPassenger(state, payload: { passenger: IPassenger }) {
    const passengers = state.passengers || [];
    const updatedPassengers = [...passengers, payload.passenger];
    state.passengers = updatedPassengers;
  },
  setOrganizationStatsLoading(state, payload: boolean) {
    state.isOrganizationStatsLoading = payload;
  },
  setOrganizationProductivityLoading(state, payload: boolean) {
    state.isOrganizationProductivityLoading = payload;
  },
  setOrganizationStats(state, payload: IOrganizationStats) {
    state.organizationStats = payload;
  },
  setOrganizationProductivity(state, payload: IOrganizationProductivity) {
    state.organizationProductivity = payload;
  },
  setCurrencyLoading(state, payload: boolean) {
    state.isCurrenciesLoading = payload;
  },
  setCurrencies(state, payload: CurrencyConversion[]) {
    state.currencies = payload;
  },
  setOrganizationSectors(state, payload: ISalesSector[]) {
    state.sectors = payload;
  },
  setOrganizationCurrency(state, payload: string[]) {
    state.curreny = payload;
  },
  setPiaProviders(state, payload: string[]) {
    state.pia_provider = payload;
  },
  setOperationsUsers(state, payload: OperationsUser[]) {
    state.operations_users = payload;
  },
  setDocumentUploadLoading(state, payload: boolean) {
    state.isDocumentUploadLoading = payload;
  },
  saveSaasOrganizations(state, organizations: ISaasEliteOrganization[]) {
    state.isFetchingOrganizations = false;
    state.saasEliteOrganization = organizations;
  },
  setEliteOrganizationStatusUpdateLoading(state, payload: boolean) {
    state.isEliteOrganizationStatusUpdating = payload;
  },
  clearOrganization(state) {
    state.organization = null;
  },
  setErrorOrganizationNotFound(state, payload: boolean) {
    state.errorOrganizationNotFound = payload;
  },
  setFetchInternalProvidersLoading(state, payload: boolean) {
    state.internalProvidersLoading = payload;
  },
  setAirblueProviders(state, payload: Array<string>) {
    state.airblue_providers = payload;
  },
  setPIAProviders(state, payload: Array<string>) {
    state.pia_providers = payload;
  },
  setUpdateFinancialProfileProvidersLoading(state, payload: boolean) {
    state.isUpdateFinancialProfileProvidersLoading = payload;
  },
  setActivePassenger(state, payload: IPassenger) {
    state.activePassenger = payload;
  },

  clearActivePassenger(state) {
    state.activePassenger = null;
  },

  updateActivePassengerSection(state, payload: Partial<IPassenger>) {
    if (state.activePassenger) {
      state.activePassenger = { ...state.activePassenger, ...payload };
    }
  },

  setFetchingActivePassenger(state, payload: boolean) {
    state.isFetchingActivePassenger = payload;
  },

  setUpdatingPassenger(state, payload: boolean) {
    state.isUpdatingPassenger = payload;
  },
};

export default mutations;
